import { useState, useCallback, useMemo } from 'react';

// Generic row-selection state for CRM grids. Works against whatever array of
// currently-loaded/filtered rows a page passes to isAllSelected/toggleAll —
// every CRM list page loads its full filtered set client-side (no server
// pagination to reconcile against), so "select all" means "all visible rows".
export function useBulkSelection() {
  const [selected, setSelected] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids) => {
    setSelected((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(ids);
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const isAllSelected = useCallback(
    (ids) => ids.length > 0 && ids.every((id) => selected.has(id)),
    [selected]
  );

  const selectedCount = selected.size;
  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  return { selected, selectedIds, selectedCount, toggle, toggleAll, clear, isAllSelected };
}
